/**
 * Phase N — /press
 *
 * Press & media page. No fabricated press mentions. If we don't have a real
 * article, we don't list one — we say so honestly and provide press-contact
 * routes plus a downloadable brand fact sheet.
 *
 * TODO: replace the "featured mentions" placeholder block with real
 * press mentions when they exist. Do NOT invent them.
 */
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import LegalReviewBanner from "@/components/LegalReviewBanner";

function Section({ eyebrow, title, id, testId, children }) {
  return (
    <section id={id} data-testid={testId} className="border-b border-stone/20 py-14 sm:py-16">
      <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">{eyebrow}</div>
      <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-ivy tracking-tight">{title}</h2>
      <div className="mt-6 max-w-3xl text-ink-soft leading-relaxed space-y-4">{children}</div>
    </section>
  );
}

export default function Press() {
  return (
    <Layout>
      <SEO
        title="Press & Media | Beyond Valencia"
        description="Media inquiries, brand fact sheet, and press-contact routes for Beyond Valencia — the national platform for transfer strategy and educational mobility."
        canonical="/press"
      />
      <LegalReviewBanner />

      {/* Hero */}
      <section className="bg-ivy text-bone" data-testid="press-hero">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-20 pb-16">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass-soft font-semibold">Press &amp; Media</div>
          <h1
            className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            data-testid="press-h1"
          >
            Media inquiries welcome.
          </h1>
          <p className="mt-6 max-w-2xl text-bone-dim text-lg leading-relaxed">
            Beyond Valencia is early in its journey. We are actively building relationships with journalists,
            researchers, and educational-mobility reporters interested in the transfer pathway.
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <Section eyebrow="Contact" title="Reach the press desk." id="contact" testId="press-contact">
          <p>
            {/* TODO: confirm mailbox routing for press@ before publishing */}
            Press inquiries:{" "}
            <a href="mailto:press@beyondvalencia.com" className="text-ivy hover:text-brass underline font-medium" data-testid="press-contact-email">
              press@beyondvalencia.com
            </a>
          </p>
          <p>
            {/* TODO: refresh response window if it changes */}
            We aim to respond to media inquiries within one (1) business day. If your deadline is tighter than that,
            note it in the subject line and we will do our best to accommodate.
          </p>
          <p>
            For general (non-press) inquiries, use{" "}
            <a href="mailto:hello@beyondvalencia.com" className="text-ivy hover:text-brass underline" data-testid="press-general-email">
              hello@beyondvalencia.com
            </a>
            .
          </p>
        </Section>

        <Section eyebrow="Fact sheet" title="Beyond Valencia at a glance." id="fact-sheet" testId="press-fact-sheet">
          <ul className="list-none space-y-3">
            <li className="grid grid-cols-1 sm:grid-cols-4 gap-2 border-b border-stone/20 pb-3">
              <span className="text-[10px] uppercase tracking-[0.2em] text-brass font-semibold sm:col-span-1">Company</span>
              {/* TODO: confirm exact legal entity name */}
              <span className="sm:col-span-3">Beyond Valencia LLC</span>
            </li>
            <li className="grid grid-cols-1 sm:grid-cols-4 gap-2 border-b border-stone/20 pb-3">
              <span className="text-[10px] uppercase tracking-[0.2em] text-brass font-semibold sm:col-span-1">Founded</span>
              <span className="sm:col-span-3">Extension of Ivy League Consulting (founded 2018)</span>
            </li>
            <li className="grid grid-cols-1 sm:grid-cols-4 gap-2 border-b border-stone/20 pb-3">
              <span className="text-[10px] uppercase tracking-[0.2em] text-brass font-semibold sm:col-span-1">Headquarters</span>
              {/* TODO: confirm HQ address before publishing */}
              <span className="sm:col-span-3">Orlando, Florida</span>
            </li>
            <li className="grid grid-cols-1 sm:grid-cols-4 gap-2 border-b border-stone/20 pb-3">
              <span className="text-[10px] uppercase tracking-[0.2em] text-brass font-semibold sm:col-span-1">Founder</span>
              <span className="sm:col-span-3">Taimur Ahmed · Harvard ALM &rsquo;17</span>
            </li>
            <li className="grid grid-cols-1 sm:grid-cols-4 gap-2 border-b border-stone/20 pb-3">
              <span className="text-[10px] uppercase tracking-[0.2em] text-brass font-semibold sm:col-span-1">Category</span>
              <span className="sm:col-span-3">Transfer strategy · educational mobility · admissions technology</span>
            </li>
            <li className="grid grid-cols-1 sm:grid-cols-4 gap-2 border-b border-stone/20 pb-3">
              <span className="text-[10px] uppercase tracking-[0.2em] text-brass font-semibold sm:col-span-1">Focus</span>
              <span className="sm:col-span-3">Florida community-college → four-year transfer pathway, expanding nationally through partnerships</span>
            </li>
            <li className="grid grid-cols-1 sm:grid-cols-4 gap-2 border-b border-stone/20 pb-3">
              <span className="text-[10px] uppercase tracking-[0.2em] text-brass font-semibold sm:col-span-1">Partner</span>
              <span className="sm:col-span-3">Ivy League Consulting (elite &amp; Ivy transfer destinations)</span>
            </li>
          </ul>
        </Section>

        <Section eyebrow="Featured mentions" title="A running list — nothing invented." id="mentions" testId="press-mentions">
          {/* TODO: replace this block with real press mentions as they are secured. Do NOT invent citations. */}
          <p>
            We keep this list intentionally short. When Beyond Valencia appears in the press, the exact publication,
            title, byline, and date will be listed here with a source URL. If you are a journalist covering the
            transfer pathway and interviewing our founder, we will add the resulting piece to this list once it is
            live.
          </p>
          <p className="text-sm text-stone italic">
            No press mentions have been added yet. This section will be populated as coverage lands.
          </p>
        </Section>

        <Section eyebrow="Story angles" title="What we're happy to talk about." id="angles" testId="press-angles">
          <ul className="list-disc pl-5 space-y-2">
            <li>The 2+2 transfer pathway as an underused strategic route into elite universities.</li>
            <li>How the Common Prerequisites Manual shapes Florida transfer outcomes — and where the system breaks.</li>
            <li>Educational mobility for first-generation, immigrant, and non-traditional students.</li>
            <li>The University of Florida&apos;s inclusion in the 2026 Forbes New Ivies list and what it means for Florida community-college students.</li>
            <li>Responsible AI use in college admissions: what it can do, what it cannot, and what students should be skeptical of.</li>
          </ul>
        </Section>

        <Section eyebrow="Brand assets" title="Logos, monograms, and imagery." id="assets" testId="press-assets">
          {/* TODO: publish downloadable brand asset zip once designer finalizes */}
          <p>
            Journalists needing brand assets (wordmark, monogram, founder photograph) can request them by emailing{" "}
            <a href="mailto:press@beyondvalencia.com" className="text-ivy hover:text-brass underline">
              press@beyondvalencia.com
            </a>
            . A downloadable brand fact sheet and asset pack will be published on this page once finalized.
          </p>
        </Section>
      </div>
    </Layout>
  );
}
